import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	cancelHandoff,
	fetchOpenHandoff,
	normalizeHandoff,
	requestHandoff,
} from "../services/assistantHandoffApi";
import type {
	IAssistantHandoff,
	IHandoffErrorSocketEvent,
	IHandoffSocketEvent,
} from "../types/assistant-handoff.types";
import {
	GATEWAY_TYPES,
	offSocketEvent,
	onSocketEvent,
} from "../utils/websocket";
import { applyHandoffEvent } from "./chatHandoffState";

/** The API's own words when it has them (429 cooldown, 409 state), else ours. */
function apiMessage(error: unknown, fallback: string): string {
	const data = (error as { response?: { data?: { message?: unknown } } })
		?.response?.data;
	const text = Array.isArray(data?.message) ? data.message[0] : data?.message;
	return typeof text === "string" && text ? text : fallback;
}

/** How long a handoff error stays up when the server gives no Retry-After. */
const DEFAULT_ERROR_TTL_MS = 2 * 60 * 1000;

/** Retry-After (seconds) from a 429, as milliseconds; else the default. */
function errorTtlMs(error: unknown): number {
	const headers = (
		error as { response?: { headers?: Record<string, unknown> } }
	)?.response?.headers;
	const seconds = Number(headers?.["retry-after"]);
	return Number.isFinite(seconds) && seconds > 0
		? seconds * 1000
		: DEFAULT_ERROR_TTL_MS;
}

function httpStatus(error: unknown): number | undefined {
	return (error as { response?: { status?: number } })?.response?.status;
}

interface ChatHandoffOptions {
	/** The server says this chat session is closed (409 on POST handoff). */
	onSessionEnded?: () => void;
}

/**
 * The customer's side of a live staff handoff for one chat session.
 *
 * State comes from `GET .../handoff` on session change, then from the
 * `chat:handoff` socket event the API pushes on every transition. The API owns
 * the state machine; this hook only mirrors it and never guesses a transition
 * locally, so a failed request leaves the UI showing the truth.
 */
export function useChatHandoff(
	sessionId: string | undefined,
	companyId?: number | null,
	{ onSessionEnded }: ChatHandoffOptions = {},
) {
	const [handoff, setHandoff] = useState<IAssistantHandoff | null>(null);
	const [isRequestingHuman, setIsRequestingHuman] = useState(false);
	// Why the last request for a person did not go through (rate limit,
	// nobody reachable). Shown in the banner; cleared by the next outcome.
	const [handoffError, setHandoffError] = useState<string | null>(null);
	// The banner is not permanent: it goes when the cooldown it describes has
	// passed (Retry-After, else 2 minutes), or earlier on the next outcome.
	const errorTtlRef = useRef(DEFAULT_ERROR_TTL_MS);
	useEffect(() => {
		if (!handoffError) return;
		const timer = setTimeout(() => setHandoffError(null), errorTtlRef.current);
		return () => clearTimeout(timer);
	}, [handoffError]);
	const clearHandoffError = useCallback(() => setHandoffError(null), []);
	const onSessionEndedRef = useRef(onSessionEnded);
	onSessionEndedRef.current = onSessionEnded;
	const sessionIdRef = useRef(sessionId);
	sessionIdRef.current = sessionId;
	// Bumped by every push/response that sets the handoff. A GET that started
	// before one of those must not overwrite the newer state when it lands.
	const pushCount = useRef(0);
	const setPushedHandoff: typeof setHandoff = (next) => {
		pushCount.current += 1;
		setHandoff(next);
	};

	/**
	 * Load the open handoff for the current session. Also called after a socket
	 * reconnect, or when the stream says a person has the chat but local state
	 * disagrees: pushes may have been missed. A response for a session the user
	 * has since left is dropped.
	 */
	const refreshHandoff = useCallback(async () => {
		const requestedFor = sessionIdRef.current;
		if (!requestedFor) return;
		const pushesBefore = pushCount.current;
		try {
			const open = await fetchOpenHandoff(requestedFor, companyId ?? null);
			const isStillCurrent =
				sessionIdRef.current === requestedFor &&
				pushCount.current === pushesBefore;
			if (isStillCurrent) setHandoff(open);
		} catch (error) {
			// A brand-new session has no row upstream yet; that is "no handoff",
			// not an error worth showing.
			console.warn("[useChatHandoff] Could not load handoff:", error);
		}
	}, [companyId]);

	useEffect(() => {
		setHandoff(null);
		setHandoffError(null);
		void refreshHandoff();
	}, [sessionId, refreshHandoff]);

	/**
	 * The API's typed result of a person request made in chat text, from the
	 * stream's `handoff` frame or a non-stream reply.
	 */
	const applyServerHandoff = useCallback(
		(raw: unknown, error: string | null) => {
			const parsed = normalizeHandoff(raw);
			if (parsed) {
				setPushedHandoff((current) =>
					applyHandoffEvent(
						current,
						parsed.sessionId
							? parsed
							: { ...parsed, sessionId: sessionIdRef.current ?? "" },
						sessionIdRef.current,
					),
				);
			}
			errorTtlRef.current = DEFAULT_ERROR_TTL_MS;
			setHandoffError(error);
		},
		[],
	);

	useEffect(() => {
		const handleHandoff = (payload: IHandoffSocketEvent) => {
			if (!payload) return;
			const parsed = normalizeHandoff(payload.handoff);
			const incoming =
				parsed && !parsed.sessionId
					? { ...parsed, sessionId: payload.sessionId }
					: parsed;
			setPushedHandoff((current) =>
				applyHandoffEvent(current, incoming, sessionIdRef.current),
			);
			if (incoming?.sessionId === sessionIdRef.current) setHandoffError(null);
		};
		const handleHandoffError = (payload: IHandoffErrorSocketEvent) => {
			if (!payload || payload.sessionId !== sessionIdRef.current) return;
			errorTtlRef.current = DEFAULT_ERROR_TTL_MS;
			setHandoffError(
				typeof payload.error === "string" && payload.error
					? payload.error
					: "We couldn't reach the Equility team just now.",
			);
		};
		onSocketEvent("chat:handoff", handleHandoff, GATEWAY_TYPES.CHAT);
		onSocketEvent("chat:handoff:error", handleHandoffError, GATEWAY_TYPES.CHAT);
		return () => {
			offSocketEvent("chat:handoff", handleHandoff, GATEWAY_TYPES.CHAT);
			offSocketEvent(
				"chat:handoff:error",
				handleHandoffError,
				GATEWAY_TYPES.CHAT,
			);
		};
	}, []);

	const requestHuman = useCallback(
		async (reason?: string) => {
			if (!sessionId) return;
			setIsRequestingHuman(true);
			setHandoffError(null);
			try {
				const created = await requestHandoff(
					sessionId,
					companyId ?? null,
					reason,
				);
				// The user may have switched session while this was in flight.
				if (created && sessionIdRef.current === sessionId) {
					setPushedHandoff(created);
				}
			} catch (error) {
				console.error("[useChatHandoff] Request failed:", error);
				// 429: the API rate-limits requests (and cools down after a
				// cancel) and says when to try again. Shown in the banner.
				if (sessionIdRef.current !== sessionId) return;
				// 409: the server already closed this chat; a new chat is the
				// only way on, which the caller shows instead of a banner.
				if (httpStatus(error) === 409) {
					onSessionEndedRef.current?.();
					return;
				}
				errorTtlRef.current = errorTtlMs(error);
				setHandoffError(
					apiMessage(
						error,
						"We couldn't reach the Equility team just now. Please try again.",
					),
				);
			} finally {
				setIsRequestingHuman(false);
			}
		},
		[sessionId, companyId],
	);

	const cancelHuman = useCallback(async () => {
		if (!sessionId) return;
		try {
			const cancelled = await cancelHandoff(sessionId, companyId ?? null);
			if (sessionIdRef.current === sessionId) setPushedHandoff(cancelled);
		} catch (error) {
			console.error("[useChatHandoff] Cancel failed:", error);
			message.error(
				apiMessage(error, "That didn't go through. Please try again."),
			);
		}
	}, [sessionId, companyId]);

	return {
		handoff,
		isRequestingHuman,
		isAwaitingHuman: handoff?.status === "AWAITING_HUMAN",
		isHumanActive: handoff?.status === "HUMAN_ACTIVE",
		requestHuman,
		cancelHuman,
		refreshHandoff,
		handoffError,
		clearHandoffError,
		applyServerHandoff,
	};
}
